import type { OptionSpec, OptionValue, Outcome, Seat } from '@campfire/engine';

/** Wire protocol version. Bump on breaking changes; host rejects mismatches. */
export const PROTOCOL_VERSION = 1;

export type Role = 'player' | 'spectator';

// ---------- client → host ----------
export type ClientMessage =
  | { readonly t: 'hello'; readonly v: number; readonly name: string; readonly role: Role; readonly token?: string }
  | { readonly t: 'start'; readonly gameId: string; readonly options?: Readonly<Record<string, unknown>> }
  | { readonly t: 'act'; readonly action: unknown; readonly seq: number }
  | { readonly t: 'end' }
  /** Owner only: change a game's settings for this room (ADR-0011). */
  | { readonly t: 'configure'; readonly gameId: string; readonly options: Readonly<Record<string, unknown>> }
  /** Owner only: name the table, as shown in the lobby and local-network listings (FR-LOBBY-06). */
  | { readonly t: 'rename'; readonly name: string }
  /** Owner only: remove a member from the table (FR-LOBBY-07). */
  | { readonly t: 'kick'; readonly memberId: string }
  /** The sender's app went to the background (away) or came back (FR-LOBBY-08). */
  | { readonly t: 'presence'; readonly away: boolean }
  /** Table chat (FR-CHAT-01). */
  | { readonly t: 'chat'; readonly text: string }
  /** iOS app: this device's APNs token, for "Your turn" notifications while away (FR-PUSH-01). */
  | { readonly t: 'push'; readonly token: string };

// ---------- host → client ----------
export interface GameInfo {
  readonly id: string;
  readonly name: string;
  readonly minSeats: number;
  readonly maxSeats: number;
  readonly optionSpecs: readonly OptionSpec[];
  /** How to play (FR-RULES-01). */
  readonly rules: readonly string[];
}

export interface MemberInfo {
  readonly id: string;
  readonly name: string;
  readonly role: Role;
  readonly connected: boolean;
  readonly seat: Seat | null;
  /** Connected but the app/tab is in the background (FR-LOBBY-08). */
  readonly away: boolean;
  /** Host clock (ms) of the member's last message or connection change; 0 when unknown. */
  readonly lastSeen: number;
  /** Signed in with a verified account (ADR-0017). Shown as a small mark; no identity is revealed. */
  readonly verified: boolean;
}

export interface LobbyMessage {
  readonly t: 'lobby';
  readonly roomCode: string;
  /** Owner-chosen table name, or "<owner>'s table" until one is set. */
  readonly roomName: string;
  readonly ownerId: string | null;
  readonly members: readonly MemberInfo[];
  readonly games: readonly GameInfo[];
  /** The room's current value for every declared setting, by game id. */
  readonly settings: Readonly<Record<string, Readonly<Record<string, OptionValue>>>>;
  readonly gameId: string | null;
}

export interface StateMessage {
  readonly t: 'state';
  readonly gameId: string;
  readonly seq: number;
  readonly seat: Seat | null;
  readonly seatNames: readonly string[];
  readonly activeSeats: readonly Seat[];
  /** Whose move is due (turn highlight); older hosts omit it, so fall back to activeSeats. */
  readonly waitingOn?: readonly Seat[];
  readonly outcome: Outcome | null;
  readonly view: unknown;
}

export interface ChatMessage {
  readonly id: number;
  readonly from: string;
  readonly name: string;
  readonly text: string;
  /** Host clock, ms since epoch. */
  readonly at: number;
}

export const CHAT_MAX_LENGTH = 280;
export const CHAT_HISTORY = 50;

export type HostMessage =
  | { readonly t: 'welcome'; readonly id: string; readonly token: string; readonly role: Role }
  | LobbyMessage
  | StateMessage
  | { readonly t: 'rejected'; readonly reason: string; readonly detail?: string }
  /** New chat lines; `history: true` replaces the client's list (sent on join). */
  | { readonly t: 'chat'; readonly messages: readonly ChatMessage[]; readonly history?: boolean }
  | { readonly t: 'error'; readonly code: ErrorCode; readonly message: string };

export type ErrorCode =
  | 'bad-json'
  | 'bad-message'
  | 'version'
  | 'not-joined'
  | 'already-joined'
  | 'not-owner'
  | 'unknown-game'
  | 'seat-count'
  | 'no-match'
  | 'not-seated'
  | 'kicked'
  | 'bad-options';

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);

/** Strict structural parse of untrusted client input. Returns null when malformed. */
export function parseClientMessage(raw: string): ClientMessage | null | 'bad-json' {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return 'bad-json';
  }
  if (!isObj(data)) return null;
  switch (data['t']) {
    case 'hello': {
      const { v, name, role, token } = data;
      if (typeof v !== 'number' || typeof name !== 'string') return null;
      if (role !== 'player' && role !== 'spectator') return null;
      if (token !== undefined && typeof token !== 'string') return null;
      const trimmed = name.trim();
      if (trimmed.length < 1 || trimmed.length > 24) return null;
      return token === undefined ? { t: 'hello', v, name: trimmed, role } : { t: 'hello', v, name: trimmed, role, token };
    }
    case 'start': {
      const { gameId, options } = data;
      if (typeof gameId !== 'string') return null;
      if (options !== undefined && !isObj(options)) return null;
      return options === undefined ? { t: 'start', gameId } : { t: 'start', gameId, options };
    }
    case 'end':
      return { t: 'end' };
    case 'configure': {
      const { gameId, options } = data;
      if (typeof gameId !== 'string' || !isObj(options)) return null;
      return { t: 'configure', gameId, options };
    }
    case 'rename': {
      const { name } = data;
      if (typeof name !== 'string') return null;
      const trimmed = name.trim().replace(/\s+/g, ' ');
      if (trimmed.length < 1 || trimmed.length > 32) return null;
      return { t: 'rename', name: trimmed };
    }
    case 'push': {
      const { token } = data;
      return typeof token === 'string' && /^[0-9a-f]{64,200}$/i.test(token) ? { t: 'push', token: token.toLowerCase() } : null;
    }
    case 'presence':
      return typeof data['away'] === 'boolean' ? { t: 'presence', away: data['away'] } : null;
    case 'chat': {
      const { text } = data;
      if (typeof text !== 'string') return null;
      const trimmed = text.trim().replace(/\s+/g, ' ');
      return trimmed.length >= 1 && trimmed.length <= CHAT_MAX_LENGTH ? { t: 'chat', text: trimmed } : null;
    }
    case 'kick': {
      const { memberId } = data;
      return typeof memberId === 'string' && memberId.length > 0 ? { t: 'kick', memberId } : null;
    }
    case 'act': {
      const { action, seq } = data;
      if (!Number.isInteger(seq) || !isObj(action) || typeof action['type'] !== 'string') return null;
      return { t: 'act', action, seq: seq as number };
    }
    default:
      return null;
  }
}

/** Host-side parse: messages from the trusted host still get a shape check. */
export function parseHostMessage(raw: string): HostMessage | null {
  try {
    const data: unknown = JSON.parse(raw);
    if (!isObj(data) || typeof data['t'] !== 'string') return null;
    return data as unknown as HostMessage;
  } catch {
    return null;
  }
}
